import * as React from "react";
import {
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Link as ALink,
  styled,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { createClinicPatientCase } from "./clinic/patients/createClinicPatientCase";
import CloseIcon from "@mui/icons-material/Close";
import { copyToClipboard } from "fe-common/utils/copyToClipboard";
import {
  ClinicActionButton,
  ClinicInnerButton,
} from "fe-web-components/clinic/clinicActionButton";

//region [[ Styles ]]

const CaseGeneratorView = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 6,
  paddingBottom: 16,
  "& .generated": {
    display: "flex",
    alignItems: "center",
    gap: 6,
    border: "1px solid #e1e1e1",
    borderRadius: 6,
    padding: 6,
    whiteSpace: "nowrap",
    minWidth: 600,
    marginBottom: 10,
    "& button": {
      marginLeft: "auto",
    },
  },
  "& .copied-msg": {
    fontWeight: 700,
    width: 72,
    textAlign: "center",
    marginLeft: "auto",
  },
  "& .generator-label": {
    fontWeight: 700,
    minWidth: 76,
  },
  "& .generator-footer": {
    marginTop: 20,
    textAlign: "center",
  },
});

//endregion [[ Styles ]]

//region [[ Props ]]

export interface CaseGeneratorProps {
  clinicName: string;
  clinicId: string;
  onClose: () => void;
  autoHideCopied?: boolean;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const CaseGenerator = ({ ...props }: CaseGeneratorProps) => {
  const [link, setLink] = useState<string>("");
  const [generating, setGenerating] = useState<boolean>(true);

  const DTitle = styled(DialogTitle)({
    "& h2": { display: "flex", alignItems: "center", gap: 6 },
  });

  const generateLink = useCallback(async () => {
    setGenerating(true);
    const newCase = await createClinicPatientCase(
      props.clinicId,
      {
        patient: {
          firstName: null,
          lastName: null,
          email: null,
          phoneNumber: null,
        },
        visitDateTime: null,
        statusList: null,
      },
      "",
    ).then();
    setLink(newCase.patient);
    setGenerating(false);
  }, [props.clinicId]);

  useEffect(() => {
    generateLink().then(() => {}); //generate first time
  }, [generateLink]);

  const codeInputRef = useRef(null);
  const linkInputRef = useRef(null);

  return (
    <Dialog open={true} onClose={() => props.onClose()} maxWidth={"md"}>
      <IconButton
        style={{ position: "absolute", top: 10, right: 10 }}
        onClick={() => props.onClose()}
        size="small"
      >
        <CloseIcon />
      </IconButton>
      <DTitle>Generated Case Link</DTitle>
      <DialogContent>
        <CaseGeneratorView>
          {generating ? (
            <div className="generated">
              <CircularProgress size={18} /> Creating a new case
            </div>
          ) : (
            <div>
              <InputCopy
                label={"Case link"}
                value={
                  <ALink href={link} target="_blank">
                    {link}
                  </ALink>
                }
                inputRef={codeInputRef}
                autoHideCopied={props.autoHideCopied}
              />
              <InputCopy
                label={"Case code"}
                value={/[^/]*$/.exec(link)[0]}
                inputRef={linkInputRef}
                autoHideCopied={props.autoHideCopied}
              />
              <div className="generator-footer">
                <ClinicActionButton
                  onClick={() => generateLink()}
                  label="Generate a new case"
                />
              </div>
            </div>
          )}
        </CaseGeneratorView>
      </DialogContent>
    </Dialog>
  );
};

const InputCopy = (props: {
  label: string;
  value: string | React.ReactElement;
  inputRef: React.MutableRefObject<HTMLDivElement>;
  autoHideCopied?: boolean;
}) => {
  const [copied, setCopied] = useState(false);
  return (
    <div className="generated">
      <label className="generator-label">{props.label}:</label>
      <div ref={props.inputRef}>{props.value}</div>
      {copied ? (
        <span className="copied-msg" onClick={() => setCopied(false)}>
          Copied
        </span>
      ) : (
        <ClinicInnerButton
          label="Copy"
          onClick={() => {
            copyToClipboard(props.inputRef.current);
            setCopied(true);
            if (props.autoHideCopied) setTimeout(() => setCopied(false), 1000);
          }}
        />
      )}
    </div>
  );
};
